/**
 * @file ExternalFromSearch.tsx
 * @brief Boardroom setup's participant picker: the ONE search also finds or
 *        adds external people (mobile parity).
 *
 * @details
 * DES (30-09-2026): the webpanel's separate "External Clients" card should
 * match mobile, where one search covers company users and external people.
 * Rendered as the picker's list header, it reads the search text:
 *
 * - A full email with a Verblizr account → the match card (photo, name,
 *   Verblizr user, voice sample, "Share meeting with them") and Add.
 * - A full email already on your roster (you, a colleague) → says so, no Add.
 * - A full email with no account → a Name field and "Add as guest", disabled
 *   until there is a name. The name is pre-filled only when the email reads
 *   like one (`guessNameFromEmail`).
 * - Anything else that is not half an email → "Add "<text>" as external
 *   participant": the text is the name, no email. With `requireEmail` (B48,
 *   DES 02-11-2026, the people rule for a new participant) it reads "Add
 *   someone: <text>" and opens Name + Email, both required.
 *
 * @references
 * - FRONTEND/src/screens/MeetingIntelligence/ExternalFromSearch.tsx (mobile)
 * - BoardroomSetupPage.tsx (the lookup and the add)
 *
 * @author DES
 * @date 30-09-2026
 */

import { useEffect, useRef, useState } from "react";
import { AddPersonForm } from "./AddPersonForm";
import { CircleCheck, Info, LoaderCircle, Mic, UserPlus } from "lucide-react";
import { Avatar } from "@/components/ui/Avatar";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Switch } from "@/components/ui/Switch";
import { cn } from "@/shared/theme/cn";
import { getInitials } from "@/shared/utils/formatters";
import { guessNameFromEmail, toNameCase } from "./guessNameFromEmail";
import {
  FULL_EMAIL_RE,
  hasExternalBlock,
  type GuestLookup,
} from "./participantSearch";

type Props = {
  query: string;
  lookup: GuestLookup;
  guestName: string;
  setGuestName: (name: string) => void;
  /** Leave both out where sharing means nothing (naming a speaker on the
   *  meeting page): the "Share meeting with them" row is then hidden. */
  share?: boolean;
  setShare?: (share: boolean) => void;
  /** e.g. "Already a participant". Shown instead of everything else. */
  blockedMessage?: string | null;
  /** B48: a typed name opens Name + Email (both required), never name-only. */
  requireEmail?: boolean;
  /** Emails already on the list (requireEmail's form refuses them). */
  takenEmails?: string[];
  onAdd: (p: { name: string; email: string }) => void;
};

export function ExternalFromSearch({
  query,
  lookup,
  guestName,
  setGuestName,
  share,
  setShare,
  blockedMessage,
  requireEmail = false,
  takenEmails,
  onAdd,
}: Props) {
  const [addingNamed, setAddingNamed] = useState(false);
  useEffect(() => setAddingNamed(false), [query]);
  const text = query.trim();
  const isEmail = FULL_EMAIL_RE.test(text);
  // Only a lookup that ANSWERED "no account" (mobile: `idle` flashed the
  // Name field on every keystroke that completed an address).
  const noAccount = isEmail && lookup.status === "notfound";

  // Pre-fill the guest's name from a name-like email, once per email, and
  // take it back if the email changes while the guess is still untouched.
  const guessedRef = useRef<{ email: string; name: string } | null>(null);
  useEffect(() => {
    const g = guessedRef.current;
    if (g && g.email !== text) {
      if (guestName === g.name) setGuestName("");
      guessedRef.current = null;
    }
    if (noAccount && !guestName && guessedRef.current === null) {
      const name = guessNameFromEmail(text);
      if (name) {
        guessedRef.current = { email: text, name };
        setGuestName(name);
      }
    }
  }, [text, noAccount, guestName, setGuestName]);

  if (!hasExternalBlock(text)) return null;

  if (!isEmail && requireEmail) {
    const name = toNameCase(text);
    return addingNamed ? (
      <div className="mb-4 space-y-2 px-1">
        <p className="text-xs font-medium uppercase tracking-widest text-muted-foreground">
          Add someone
        </p>
        <AddPersonForm
          initialName={name}
          takenEmails={takenEmails}
          onCancel={() => setAddingNamed(false)}
          onAdd={(p) => {
            setAddingNamed(false);
            onAdd(p);
          }}
        />
      </div>
    ) : (
      <button
        type="button"
        onClick={() => setAddingNamed(true)}
        className="mb-3 flex w-full items-center gap-4 rounded-full py-1 pr-4 text-left transition-colors hover:bg-muted/60"
        aria-label={`Add someone: ${name}`}
      >
        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-muted">
          <UserPlus className="h-5 w-5 text-brand" />
        </span>
        <span className="min-w-0 flex-1 text-[15px] font-medium leading-5 text-foreground">
          Add someone: <span className="font-semibold">{name}</span>
          <span className="text-xs font-normal text-muted-foreground"> - name and email</span>
        </span>
      </button>
    );
  }

  if (!isEmail) {
    const name = toNameCase(text);
    return (
      <button
        type="button"
        onClick={() => onAdd({ name, email: "" })}
        className="mb-3 flex w-full items-center gap-4 rounded-full py-1 pr-4 text-left transition-colors hover:bg-muted/60"
        aria-label={`Add ${name} as external participant`}
      >
        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-muted">
          <UserPlus className="h-5 w-5 text-brand" />
        </span>
        <span className="min-w-0 flex-1 text-[15px] font-medium leading-5 text-foreground">
          Add <span className="font-semibold">“{name}”</span> as external
          participant
          <span className="text-xs font-normal text-muted-foreground">
            {" "}
            - click to add
          </span>
        </span>
      </button>
    );
  }

  let body: React.ReactNode;
  if (blockedMessage) {
    body = <StatusLine brand>{blockedMessage}</StatusLine>;
  } else if (lookup.status === "idle") {
    return null;
  } else if (lookup.status === "checking") {
    body = (
      <p className="flex items-center gap-2 text-sm text-muted-foreground">
        <LoaderCircle className="h-4 w-4 animate-spin" />
        Checking Verblizr…
      </p>
    );
  } else if (lookup.status === "inlist") {
    body = (
      <StatusLine brand>
        {lookup.reason === "self"
          ? "That's you. You're already the host."
          : `${lookup.name || "They"} ${lookup.name ? "is" : "are"} a company user. Tick them in the list below.`}
      </StatusLine>
    );
  } else if (lookup.status === "found") {
    body = (
      <>
        <div className="overflow-hidden rounded-[1.15rem] bg-muted">
          <div className="flex items-center gap-3 px-3 py-2.5">
            <Avatar
              src={lookup.profileImageUrl}
              alt={lookup.name}
              initials={getInitials(lookup.name || text)}
              size="md"
            />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-foreground">
                {lookup.name}
              </p>
              <p className="mt-0.5 flex items-center gap-1 truncate text-xs text-muted-foreground">
                <CircleCheck className="h-3.5 w-3.5 shrink-0 text-brand" />
                Verblizr user
              </p>
            </div>
            <Mic
              className={cn(
                "h-4 w-4 shrink-0",
                lookup.hasEnrolledVoice ? "text-success" : "text-destructive",
              )}
              aria-label={
                lookup.hasEnrolledVoice
                  ? "Voice enrolled"
                  : "Voice not enrolled"
              }
            />
          </div>
          {setShare ? (
            <label className="flex items-center justify-between gap-3 border-t border-border/60 px-3 py-2.5">
              <span className="min-w-0 text-sm text-foreground">
                Share meeting with them
              </span>
              <Switch
                checked={!!share}
                onCheckedChange={setShare}
                aria-label="Share meeting with them"
              />
            </label>
          ) : null}
        </div>
        <AddButton
          label="Add participant"
          onClick={() =>
            onAdd({ name: lookup.name || guestName.trim(), email: text })
          }
        />
      </>
    );
  } else {
    const canAdd = guestName.trim().length > 0;
    body = (
      <>
        <StatusLine>
          No Verblizr account for this email. Add them as a guest.
        </StatusLine>
        <Input
          capitalizeFirst
          value={guestName}
          onChange={(e) => setGuestName(e.target.value)}
          placeholder="Their name"
          aria-label="Their name"
          autoFocus={!guestName}
          onKeyDown={(e) => {
            if (e.key === "Enter" && canAdd)
              onAdd({ name: toNameCase(guestName), email: text });
          }}
        />
        <AddButton
          label="Add as guest"
          disabled={!canAdd}
          onClick={() => onAdd({ name: toNameCase(guestName), email: text })}
        />
      </>
    );
  }

  return (
    <div className="mb-4 space-y-3 px-1">
      <p className="text-xs font-medium uppercase tracking-widest text-muted-foreground">
        External participant
      </p>
      {body}
    </div>
  );
}

function StatusLine({
  brand,
  children,
}: {
  brand?: boolean;
  children: React.ReactNode;
}) {
  return (
    <p className="flex items-start gap-2 text-sm text-muted-foreground">
      <Info
        className={cn(
          "mt-0.5 h-4 w-4 shrink-0",
          brand ? "text-brand" : "text-muted-foreground",
        )}
        aria-hidden
      />
      <span>{children}</span>
    </p>
  );
}

function AddButton({
  label,
  disabled,
  onClick,
}: {
  label: string;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <Button
      type="button"
      className="h-11 w-full rounded-full"
      disabled={disabled}
      onClick={onClick}
    >
      <UserPlus className="mr-1.5 h-4 w-4" />
      {label}
    </Button>
  );
}
